// src/Login.js
import React, { useState, useEffect, useContext } from 'react';
import {
  getAuth,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  signOut
} from 'firebase/auth';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { useNavigate, useLocation } from 'react-router-dom';
import { AppContext } from './AppContext';

const Login = () => {
    const { isAllowed,
        setIsAllowed } = useContext(AppContext);
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [error, setError]       = useState('');

  const auth     = getAuth();
  const db       = getFirestore();
  const navigate = useNavigate();
  const location = useLocation();

  /* deep-link hop back to mobile (optional, mirrors LoginScreen.js) */
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const source       = searchParams.get('source');
    const path         = location.pathname;

    if (source === 'app' && path.includes('/mydat/profile/')) {
      const username = path.split('/mydat/profile/')[1];
      if (username) window.location.href = `myapp://profile/${username}`;
    }
  }, [location]);

  /* helpers */
  const handlePasswordReset = async () => {
    if (!email) return setError('Please enter your e-mail first');
    try {
      await sendPasswordResetEmail(auth, email);
      alert('Check your e-mail for a reset link');
    } catch (e) {
      console.error(e.message);
      setError(e.message);
    }
  };

  const handleLogin = async () => {
    setError('');
    if (!email || !password) {
      return setError('Please enter both e-mail and password');
    }

    try {
      // 1) Sign in
      const userCredential = await signInWithEmailAndPassword(auth, email, password);

      // 2) Check allow-list in Firestore
      const allowedRef = doc(db, 'portalAllowedUsers', 'allowedUsers');
      const allowedSnap = await getDoc(allowedRef);

      const allowedUsers = allowedSnap.exists()
        ? allowedSnap.data().users || []
        : [];

      if (!allowedUsers.includes(userCredential.user.uid)) {
        // not allowed → sign out and show error
        setIsAllowed(false);
      } else {
        setIsAllowed(true);
      }

      // 3) Allowed → proceed
      navigate('/');
    } catch (e) {
      console.error(e.message);
      setError(e.message);
    }
  };

  /** simple reusable button */
  const Button = ({ title, onClick, style = {} }) => (
    <button onClick={onClick} style={{ ...styles.button, ...style }}>
      {title}
    </button>
  );

  return (
    <div className="homepage-container">
      <div style={styles.form}>
        <h1 style={styles.header}>Login</h1>
        {error && <p style={styles.error}>{error}</p>}

        <input
          type="email"
          placeholder="Email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          style={styles.input}
        />
        <input
          type="password"
          placeholder="Password"
          value={password}
          onChange={e => setPassword(e.target.value)}
          style={styles.input}
        />
        <Button
          title="Login"
          onClick={handleLogin}
          style={{ border: '2px solid #62fbfa', marginBottom: 16 }}
        />
      </div>

      {/* inline styles (kept consistent with ViewDAT + original Login layout) */}
<style>{`
  :root{
    --brand-top:#fe24ad;
    --brand-bot:#a410e8;
    --brand-grad:linear-gradient(135deg,var(--brand-top),var(--brand-bot));
    --brand-top-20:rgba(254,36,173,.2);
    --brand-bot-40:rgba(164,16,232,.4);
  }

  *{margin:0;padding:0;box-sizing:border-box;}
  html,body{
    width:100vw;height:100vh;overflow:hidden;
    background:#000;font-family:sans-serif;color:#fff;
  }

  .homepage-container{
    width:100vw;height:100vh;
    background:
      radial-gradient(800px 600px at 15% 10%, var(--brand-top-20), transparent 60%),
      radial-gradient(700px 500px at 85% 90%, var(--brand-bot-40), transparent 60%),
      #000;
    display:flex;justify-content:center;align-items:flex-start;
    padding-top:10vh;
    animation:fadeInPage 1s ease-in-out;
  }

  @keyframes fadeInPage{from{opacity:0;}to{opacity:1;}}
`}</style>

    </div>
  );
};

/* component-scoped JS-style objects */
const styles = {
  form: {
    width: '85%',
    maxWidth: 400,
    padding: 20,
    borderRadius: 12,
    background: '#050008',
    border: `1px solid var(--brand-bot-40)`,
    boxShadow: '0 4px 20px rgba(0,0,0,.5)'
  },
  header: {
    fontSize: '2rem',
    fontWeight: 700,
    marginBottom: 16,
    textAlign: 'center',
    background: 'var(--brand-grad)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent'
  },
  input: {
    width: '100%',
    height: 42,
    marginBottom: 16,
    padding: '0 10px',
    fontSize: '1rem',
    border: `1px solid var(--brand-bot-40)`,
    borderRadius: 6,
    background: '#000',
    color: '#fff'
  },
  error: {
    color: 'var(--brand-top)',
    marginBottom: 16,
    fontSize: '1rem',
    textAlign: 'center'
  },
  button: {
    width: '100%',
    padding: 10,
    borderRadius: 8,
    background: 'var(--brand-grad)',
    cursor: 'pointer',
    color: '#fff',
    fontSize: '1rem',
    fontWeight: 700,
    border: 'none',
    boxShadow: '0 0 0 1px var(--brand-bot-40) inset',
    transition: 'filter .2s ease'
  },
  badgeWrapper: (top) => ({
    position: 'relative',
    display: 'flex',
    justifyContent: 'center',
    marginTop: top,
    cursor: 'pointer'
  }),
  badgeImage: {
    width: 150,
    height: 50,
    objectFit: 'contain'
  }
};


export default Login;
